import React, { useEffect, useState, useRef } from 'react';
import { Carousel, Container, Row, Col, Card, Button, Modal, Dropdown, Nav, Spinner, Alert } from 'react-bootstrap';
import { FaCalendar, FaClock, FaFacebookF, FaInstagram, FaTwitter, FaLinkedinIn, FaWhatsapp, FaPhone } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import './Home.css';
import { db, storage } from '../firebase';
import { ref as dbRef, get, child } from 'firebase/database';
import { ref as storageRef, listAll, getDownloadURL } from 'firebase/storage';
import CircularCoursesGrid from '../components/CircularCoursesGrid';
import Form from '../components/Form'; 
import Rectop from '../rectop';
import Teacher from '../Teacher';
import emailjs from '@emailjs/browser';
import NewsAndEvents from '../components/NewsAndEvents';

const headerLogo = '/assets/gsk.jpg';

const socialIcons = [
  { icon: <FaFacebookF />, url: "https://www.facebook.com/share/19sFpSGG8u/" },
  { icon: <FaInstagram />, url: "https://www.instagram.com/gskcollege221505?igsh=MW95NnBlemRuODZkNA==" },
  { icon: <FaTwitter />, url: "https://twitter.com" },
  { icon: <FaLinkedinIn />, url: "https://www.linkedin.com/in/abhishek-prajapati-09a455271/" },
];

// Update the slide path constants
const slide1ImagePath = '/assets/1.jpg';
const slide2ImagePath = '/assets/3.jpg';
const slide3ImagePath = '/assets/7.jpg'; // Make sure this file exists in public/assets folder

const featuredPrograms = [
  {
    image: 'https://images.unsplash.com/photo-1513258496099-48168024aec0?auto=format&fit=crop&w=600&q=80',
    title: 'O Level',
    provider: 'NIELIT Certified',
    type: 'Professional Course',
    description: 'O Level is a foundation course in computer applications, recognized by the Government of India. Ideal for students seeking a career in IT.',
    highlights: [
      'Government recognized certification',
      'Covers IT fundamentals, programming, and internet',
      'Eligibility: 10+2 or ITI Certificate',
      'Duration: 1 year',
      'Practical & project work included'
    ]
  },
  {
    image: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?auto=format&fit=crop&w=600&q=80',
    title: 'A Level',
    provider: 'NIELIT Certified',
    type: 'Advanced Professional Course',
    description: 'A Level is an advanced diploma in computer applications, suitable for graduates and IT professionals aiming for higher positions.',
    highlights: [
      'Advanced programming & database concepts',
      'Eligibility: O Level or Graduation',
      'Duration: 1.5 years',
      'Project work and practicals',
      'Industry-oriented curriculum'
    ]
  },
  {
    image: 'https://images.unsplash.com/photo-1503676382389-4809596d5290?auto=format&fit=crop&w=600&q=80',
    title: 'ADCA',
    provider: 'Advanced Diploma',
    type: 'Diploma Course',
    description: 'ADCA (Advanced Diploma in Computer Applications) covers all major aspects of computer technology and office automation.',
    highlights: [
      'Covers MS Office, Tally, Internet, C/C++',
      'Eligibility: 10th pass',
      'Duration: 1 year',
      'Practical training',
      'Job-oriented modules'
    ]
  },
  {
    image: 'https://images.unsplash.com/photo-1519125323398-675f0ddb6308?auto=format&fit=crop&w=600&q=80',
    title: 'DCA',
    provider: 'Diploma',
    type: 'Diploma Course',
    description: 'DCA (Diploma in Computer Applications) is a short-term course for beginners to learn computer basics and office tools.',
    highlights: [
      'MS Office, Internet, Windows basics',
      'Eligibility: 10th pass',
      'Duration: 6 months',
      'Practical sessions',
      'Ideal for office jobs'
    ]
  }
];

const Home = () => {
  const navigate = useNavigate();
  const [latestNews, setLatestNews] = useState([]);
  const [campusEvents, setCampusEvents] = useState([]);
  const [loadingNews, setLoadingNews] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [showScroll, setShowScroll] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showProgramModal, setShowProgramModal] = useState(false);
  const [modalProgram, setModalProgram] = useState(null);

  // Gallery states
  const [galleryImages, setGalleryImages] = useState({});
  const [activeCategory, setActiveCategory] = useState('All');
  const [galleryLoading, setGalleryLoading] = useState(true);
  const [galleryError, setGalleryError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [rotation, setRotation] = useState(0);

  const contactRef = React.useRef(null);

  const categories = [
    'All', 'Certificate', 'Training', 'Achievements',
    'Events', 'Workshops', 'Cultural'
  ];

  // Enhanced clean caption function
  const cleanCaption = (filename) => {
    let name = filename.replace(/\.[^/.]+$/, "")
                       .replace(/[0-9]/g, '')
                       .replace(/_/g, ' ')
                       .trim();
    
    return name.split(' ')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ')
              .replace(/\s+/g, ' ');
  };

  // Fetch gallery images from Firebase
  const fetchGalleryImages = async () => {
    try {
      setGalleryLoading(true);
      const imagesData = {};

      await Promise.all(categories.map(async (category) => {
        if (category === 'All') return;
        
        const folderRef = storageRef(storage, `gallery/${category.toLowerCase()}`);
        try {
          const result = await listAll(folderRef);

          imagesData[category] = await Promise.all(
            result.items.map(async (itemRef) => ({
              url: await getDownloadURL(itemRef),
              name: itemRef.name,
              category
            }))
          );
        } catch (error) {
          console.warn(`No images found for category: ${category}`);
          imagesData[category] = [];
        }
      }));

      setGalleryImages(imagesData);
    } catch (err) {
      setGalleryError('Failed to load gallery. Please try again later.');
      console.error('Gallery error:', err);
    } finally {
      setGalleryLoading(false);
    }
  };

  const getFilteredImages = () => {
    return activeCategory === 'All' 
      ? Object.values(galleryImages).flat() 
      : galleryImages[activeCategory] || [];
  };

  const openImageModal = (image) => {
    setSelectedImage(image);
    setRotation(0);
  };

  const rotateImage = () => setRotation(prev => (prev + 90) % 360);

  // Animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  useEffect(() => {
    const fetchNotices = async () => {
      try {
        const databaseRef = dbRef(db);
        const noticesSnapshot = await get(child(databaseRef, 'notices'));
        
        if (noticesSnapshot.exists()) {
          const data = noticesSnapshot.val();
          const noticesList = Object.entries(data).map(([id, notice]) => ({
            id,
            ...notice
          }));

          const sortedNotices = noticesList.sort((a, b) => new Date(b.date) - new Date(a.date));

          const events = sortedNotices
            .filter(notice => notice.category === 'Cultural')
            .slice(0, 5);
          setCampusEvents(events);
          setLoadingEvents(false);

          const news = sortedNotices
            .filter(notice => notice.category === 'General')
            .slice(0, 3);
          setLatestNews(news);
          setLoadingNews(false);
        } else {
          setCampusEvents([]);
          setLatestNews([]);
          setLoadingEvents(false);
          setLoadingNews(false);
        }
      } catch (error) {
        console.error("Error fetching notices:", error);
        setLoadingEvents(false);
        setLoadingNews(false);
      }
    };

    fetchNotices();
    fetchGalleryImages();
  }, []);

  useEffect(() => {
    const checkScrollTop = () => {
      if (!showScroll && window.pageYOffset > 400) {
        setShowScroll(true);
      } else if (showScroll && window.pageYOffset <= 400) {
        setShowScroll(false);
      }
    };
    
    window.addEventListener('scroll', checkScrollTop);
    return () => {
      window.removeEventListener('scroll', checkScrollTop);
    };
  }, [showScroll]);

  const handleApplyNow = () => {
    const contactSection = document.getElementById('contact-section');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
    setShowContactForm(true);
  };

  const handleLearnMore = (program) => {
    setModalProgram(program);
    setShowProgramModal(true);
  };

  const handleCloseProgramModal = () => {
    setShowProgramModal(false);
    setModalProgram(null);
  };

  const handleContactUsClick = () => {
    const contactSection = document.getElementById('contact-section');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
    setShowContactForm(true);
  };

  const handleWhatsAppClick = () => {
    window.open('https://wa.me/9807664383', '_blank');
  };

  const handlePhoneClick = () => {
    window.open('tel:9807664383');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return new Date(dateString).toLocaleDateString(undefined, options);
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString;
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const menuItems = [
    {
      label: 'ABOUT US',
      path: '/about',
      dropdown: true,
      submenu: [
        { label: 'Our Story', path: '/about' },
        { label: 'Mission & Vision', path: '/about/mission' },
        { label: 'Leadership', path: '/about/leadership' },
      ]
    },
    {
      label: 'COURSES',
      path: '/courses',
      dropdown: true,
      submenu: [
        { label: 'Computer Courses', path: '/courses/computer' },
        { label: 'Management Courses', path: '/courses/management' },
        { label: 'Vocational Courses', path: '/courses/vocational' },
        { label: 'Short Term Courses', path: '/courses/short-term' },
      ]
    },
    {
      label: 'GET IN TOUCH',
      path: '/get-in-touch',
      dropdown: true,
      submenu: [
        { label: 'Inquiry Form', path: '/get-in-touch/inquiry' },
        { label: 'Support', path: '/get-in-touch/support' },
      ]
    },
    {
      label: 'STUDENT',
      path: '/student',
      dropdown: true,
      submenu: [
        { label: 'Student Login', path: '/student/login' },
        { label: 'Student Portal', path: '/student/portal' },
        { label: 'Results', path: '/student/results' },
      ]
    },
    {
      label: 'CENTER SECTION',
      path: '/center-section',
      dropdown: true,
      submenu: [
        { label: 'Center Login', path: '/center-section/login' },
        { label: 'Center Portal', path: '/center-section/portal' },
      ]
    },
    {
      label: 'FRANCHISE',
      path: '/franchise',
      dropdown: true,
      submenu: [
        { label: 'Become a Franchise', path: '/franchise/apply' },
        { label: 'Franchise Login', path: '/franchise/login' },
      ]
    },
    {
      label: 'MORE',
      path: '/more',
      dropdown: true,
      submenu: [
        { label: 'Gallery', path: '/gallery' },
        { label: 'Study Material', path: '/downloads' },
        { label: 'FAQ', path: '/faq' },
      ]
    },
  ];

  const computerCourses = [
    { label: 'O Level', path: '/courses/computer/o-level' },
    { label: 'A Level', path: '/courses/computer/a-level' },
    { label: 'ADCA', path: '/courses/computer/adca' },
    { label: 'DCA', path: '/courses/computer/dca' },
  ];

  const managementCourses = [
    { label: 'BBA', path: '/courses/management/bba' },
    { label: 'MBA', path: '/courses/management/mba' },
    { label: 'HRM', path: '/courses/management/hrm' },
    { label: 'Marketing', path: '/courses/management/marketing' },
  ];

  const vocationalCourses = [
    { label: 'Hardware & Networking', path: '/courses/vocational/hardware' },
    { label: 'Graphic & Web Design', path: '/courses/vocational/design' },
  ];

  const shortTermCourses = [
    { label: 'MS Office', path: '/courses/short-term/ms-office' },
    { label: 'Tally', path: '/courses/short-term/tally' },
    { label: 'Photoshop', path: '/courses/short-term/photoshop' },
  ];

  function CoursesDropdown() {
    return (
      <Dropdown className="d-inline-block">
        <Dropdown.Toggle
          variant="link"
          className="dropdown-toggle"
          id="dropdown-courses"
        >
          Courses <span className="dropdown-arrow">▼</span>
        </Dropdown.Toggle>
        <Dropdown.Menu>
          <Dropdown.Header>Computer Courses</Dropdown.Header>
          {computerCourses.map((course, idx) => (
            <Dropdown.Item key={`comp-${idx}`} onClick={() => navigate(course.path)}>
              {course.label}
            </Dropdown.Item>
          ))}
          <Dropdown.Divider />
          <Dropdown.Header>Management Courses</Dropdown.Header>
          {managementCourses.map((course, idx) => (
            <Dropdown.Item key={`mgmt-${idx}`} onClick={() => navigate(course.path)}>
              {course.label}
            </Dropdown.Item>
          ))}
          <Dropdown.Divider />
          <Dropdown.Header>Vocational Courses</Dropdown.Header>
          {vocationalCourses.map((course, idx) => (
            <Dropdown.Item key={`voc-${idx}`} onClick={() => navigate(course.path)}>
              {course.label}
            </Dropdown.Item>
          ))}
          <Dropdown.Divider />
          <Dropdown.Header>Short Term Courses</Dropdown.Header>
          {shortTermCourses.map((course, idx) => (
            <Dropdown.Item key={`short-${idx}`} onClick={() => navigate(course.path)}>
              {course.label}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    );
  }

  const handleContactFormSubmit = async (formData) => {
    try {
      const response = await emailjs.send(
        'service_r4rdqel',
        'template_2nemcjs',
        {
          from_name: `${formData.firstName} ${formData.lastName}`,
          from_email: formData.email,
          message: formData.message
        },
        'r4h-QQHVf4PaVzNiV'
      );

      if (response.status === 200) {
        alert('Thank you for your message! We will contact you soon.');
        setShowContactForm(false);
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      alert('Failed to send message. Please try again later.');
    }
  };

  return (
    <div className="home-container">
      {/* Floating Contact Icons */}
      <div className="floating-contact-icons">
        <div className="floating-icon whatsapp-icon" onClick={handleWhatsAppClick}>
          <FaWhatsapp />
          <span className="icon-tooltip">Chat on WhatsApp</span>
        </div>
        <div className="floating-icon phone-icon" onClick={handlePhoneClick}>
          <FaPhone />
          <span className="icon-tooltip">Call Us</span>
        </div>
      </div>

      {/* 1. White Main Header */}
      <div className="main-header">
        <Container fluid>
          <Row className="align-items-center">
            <Col xs="auto" className="d-flex align-items-center justify-content-center header-logo-col">
              <img src={headerLogo} alt="Logo" className="header-logo" />
            </Col>
            <Col className="d-flex flex-column justify-content-center header-title-col">
              <div className="header-title-main">GSK Institute</div>
              <div className="header-title-sub">Management & Computer Technology</div>
            </Col>
            <Col xs="auto" className="d-none d-md-flex align-items-center justify-content-center header-social-col">
              <div className="header-social-icons">
                {socialIcons.map((item, idx) => (
                  <a
                    key={idx}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="header-social-link"
                    aria-label={`Social ${idx}`}
                  >
                    {item.icon}
                  </a>
                ))}
              </div>
            </Col>
            <Col xs="auto" className="d-none d-md-flex align-items-center justify-content-end header-timing-col">
              <div className="header-timing">
                We Are Open <FaCalendar className="timing-icon" /> Mon-Sat <FaClock className="timing-icon" /> 7:00-06:30
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* 2. Maroon Navbar */}
      <div className="custom-navbar">
        <Container fluid>
          <Row className="align-items-center">
            <Col>
              <nav>
                <CoursesDropdown />
                {menuItems.filter(item => item.label !== 'COURSES').map((item, idx) =>
                  item.dropdown ? (
                    <Dropdown key={idx} className="d-inline-block">
                      <Dropdown.Toggle
                        variant="link"
                        className="dropdown-toggle"
                        id={`dropdown-${item.label}`}
                      >
                        {item.label} <span className="dropdown-arrow">▼</span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        {item.submenu && item.submenu.map((sub, subIdx) => (
                          <Dropdown.Item key={subIdx} onClick={() => navigate(sub.path)}>
                            {sub.label}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  ) : (
                    <span
                      key={idx}
                      className="nav-item"
                      onClick={() => navigate(item.path)}
                    >
                      {item.label}
                    </span>
                  )
                )}
                <span
                  className="nav-item"
                  onClick={handleContactUsClick}
                >
                  CONTACT US
                </span>
              </nav>
            </Col>
          </Row>
        </Container>
      </div>

      {/* 3. Hero Carousel */}
      <Carousel 
        fade 
        className="main-carousel" 
        controls={false} 
        indicators={false} 
        interval={2000}
        style={{ height: '90vh', overflow: 'hidden' }} // Changed to 90vh height
      >
        <Carousel.Item>
          <div className="carousel-slide" style={{ height: '90vh' }}> {/* Added fixed height */}
            <img
              src={slide1ImagePath}
              alt="Carousel 1"
              className="carousel-image"
              loading="eager"
              style={{ 
                width: '100%',
                height: '100%',
                objectFit: 'fill', // Changed to fill
                objectPosition: 'center'
              }}
            />
            <div className="carousel-overlay">
              <div className="carousel-content">
                <h2>GSK College</h2>
                <p>The Teachers Training Program is applicable for GSK College of Management & Computer Technology.</p>
                <Button variant="primary" className="cta-button" onClick={handleApplyNow}>
                  Enquiry Now
                </Button>
              </div>
            </div>
          </div>
        </Carousel.Item>
        <Carousel.Item>
          <div className="carousel-slide" style={{ height: '90vh' }}> {/* Added fixed height */}
            <img
              src={slide2ImagePath}
              alt="Carousel 2"
              className="carousel-image"
              style={{ 
                width: '100%',
                height: '100%',
                objectFit: 'fill', // Changed to fill
                objectPosition: 'center'
              }}
            />
            <div className="carousel-overlay">
              <div className="carousel-content">
                <h2>Excellence in Education</h2>
                <p>State-of-the-art facilities and expert faculty</p>
                <Button variant="primary" className="cta-button" onClick={handleApplyNow}>
                  Make an Enquiry
                </Button>
              </div>
            </div>
          </div>
        </Carousel.Item>
        <Carousel.Item>
          <div className="carousel-slide" style={{ height: '90vh' }}> {/* Added fixed height */}
            <img
              src={slide3ImagePath}
              alt="Carousel 3"
              className="carousel-image"
              style={{ 
                width: '100%',
                height: '100%',
                objectFit: 'fill', // Changed to fill
                objectPosition: 'center'
              }}
            />
            <div className="carousel-overlay">
              <div className="carousel-content">
                <h2>GSK College</h2>
                <p>Sports, arts, and cultural activities</p>
                <Button variant="primary" className="cta-button" onClick={handleApplyNow}>
                  Contact Us
                </Button>
              </div>
            </div>
          </div>
        </Carousel.Item>
      </Carousel>

      {/* Updated Marquee Section */}
      <div className="marquee-wrapper" style={{
        background: '#b71c1c',
        padding: '12px 0',
        width: '100%',
        marginTop: '-5px',
        position: 'relative',
        zIndex: 10
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <marquee scrollAmount="5" style={{
            fontSize: '16px',
            color: '#fff',
            fontWeight: 500,
            letterSpacing: '0.3px'
          }}>
            <div style={{ display: 'inline-flex', gap: '30px' }}>
              <span>📢 Admission Open for 2025-26</span>
              <span>🎓 New Batches Starting Soon</span>
              <span>🏆 100% Placement Assistance</span>
              <span>📞 Call: 9807664383</span>
              <span>✉️ aitraining2611@gmail.com</span>
            </div>
          </marquee>
        </div>
      </div>

      {/* News and Events Section */}
      <section className="news-events-section">
        <Container>
          <div className="section-header">
            <h2 className="section-title">Campus Updates</h2>
            <p className="section-subtitle">Stay updated with the latest news and events</p>
          </div>
          <NewsAndEvents
            campusEvents={campusEvents}
            latestNews={latestNews}
            loadingEvents={loadingEvents}
            loadingNews={loadingNews}
            formatDate={formatDate}
          />
        </Container>
      </section>

      {/* Enhanced Gallery Section - Integrated from Gallery Component */}
      <section className="gallery-enhanced-section">
        <Container fluid className="py-4">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <div className="section-header">
              <h2 className="section-title">
                <span>Campus</span> Gallery
              </h2>
              <p className="section-subtitle">
                Experience the vibrant life at GSK College
              </p>
            </div>
          </motion.div>

          <Nav variant="pills" className="justify-content-center mb-4 category-nav">
            {categories.map((category) => (
              <Nav.Item key={category}>
                <Nav.Link
                  eventKey={category}
                  active={activeCategory === category}
                  onClick={() => setActiveCategory(category)}
                  className="category-link"
                >
                  {category}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>

          {galleryLoading ? (
            <div className="text-center my-5 py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading beautiful memories...</p>
            </div>
          ) : galleryError ? (
            <Alert variant="danger" className="text-center">
              {galleryError}
            </Alert>
          ) : (
            <Row className="g-4">
              {getFilteredImages().length === 0 ? (
                <Col className="text-center py-5">
                  <motion.div
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ repeat: Infinity, duration: 2 }}
                  >
                    <h5 className="text-muted">No images found in this category</h5>
                  </motion.div>
                </Col>
              ) : (
                getFilteredImages().map((image, index) => (
                  <Col key={index} xs={12} sm={6} md={4} lg={3}>
                    <motion.div
                      variants={cardVariants}
                      initial="hidden"
                      animate="visible"
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ y: -5 }}
                    >
                      <Card className="gallery-card h-100" style={{ 
                        overflow: 'hidden',
                        border: 'none',
                        borderRadius: '12px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }}>
                        <div className="image-wrapper" 
                          onClick={() => openImageModal(image)}
                          style={{ 
                            cursor: 'pointer',
                            position: 'relative',
                            aspectRatio: '1',
                            overflow: 'hidden'
                          }}
                        >
                          <img
                            src={image.url}
                            alt={cleanCaption(image.name)}
                            className="gallery-image"
                            loading="lazy"
                            style={{ 
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              transition: 'transform 0.3s ease'
                            }}
                          />
                          <div className="image-overlay"
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: 'rgba(0,0,0,0.9)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              opacity: 0,
                              transition: 'all 0.3s ease'
                            }}
                          >
                            <span style={{ 
                              color: '#fff',
                              fontSize: '1.2rem',
                              fontWeight: '600',
                              letterSpacing: '0.5px'
                            }}>
                              Click to View
                            </span>
                          </div>
                        </div>
                      </Card>
                    </motion.div>
                  </Col>
                ))
              )}
            </Row>
          )}

          {/* Modal with details */}
          <Modal 
            show={!!selectedImage} 
            onHide={() => setSelectedImage(null)}
            size="lg"
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>
                {selectedImage && cleanCaption(selectedImage.name)}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="text-center">
              {selectedImage && (
                <motion.div
                  animate={{ rotate: rotation }}
                  transition={{ type: 'spring', stiffness: 100 }}
                >
                  <img
                    src={selectedImage.url}
                    alt={cleanCaption(selectedImage.name)}
                    style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                  />
                </motion.div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button 
                variant="outline-primary" 
                onClick={rotateImage}
              >
                Rotate
              </Button>
              <Button 
                variant="secondary" 
                onClick={() => setSelectedImage(null)}
              >
                Close
              </Button>
            </Modal.Footer>
          </Modal>
        </Container>
      </section>

      {/* Circular Courses Grid */}
      <section className="circular-courses-section">
        <Container>
          <div className="section-header">
            <h2 className="section-title">Our Courses</h2>
            <p className="section-subtitle">Explore our wide range of programs</p>
          </div>
          <CircularCoursesGrid />
        </Container>
      </section>

      {/* Rectop and Teacher Components */}
      <section className="components-section">
        <Container>
          <Rectop />
          <Teacher />
        </Container>
      </section>

      {/* Featured Programs - Image Gallery Style */}
      <section className="featured-programs-section">
        <Container>
          <div className="section-header">
            <h2 className="section-title">Featured Programs</h2>
            <p className="section-subtitle">Our most popular career-oriented courses</p>
          </div>
          <div className="programs-gallery">
            <Row>
              {featuredPrograms.map((program, idx) => (
                <Col lg={3} md={6} className="mb-4" key={idx}>
                  <div className="gallery-program-card" onClick={() => handleLearnMore(program)}>
                    <div className="gallery-image-container">
                      <img
                        src={program.image}
                        alt={program.title}
                        className="gallery-program-image"
                        onError={e => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/300x200?text=Program+Image';
                        }}
                      />
                      <div className="gallery-overlay">
                        <div className="gallery-content">
                          <h5>{program.title}</h5>
                          <p>{program.type}</p>
                          <Button variant="light" size="sm">
                            Learn More
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div className="gallery-program-info">
                      <h6>{program.title}</h6>
                      <span className="gallery-program-provider">{program.provider}</span>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        </Container>
      </section>

      {/* Program Details Modal */}
      <Modal show={showProgramModal} onHide={handleCloseProgramModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{modalProgram?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalProgram && (
            <>
              <img
                src={modalProgram.image}
                alt={modalProgram.title}
                className="modal-program-image"
                onError={e => {
                  e.target.onerror = null;
                  e.target.src = 'https://via.placeholder.com/300x200?text=Program+Image';
                }}
              />
              <h5 className="modal-program-provider">{modalProgram.provider}</h5>
              <p className="modal-program_description">{modalProgram.description}</p>
              <ul className="modal-program-highlights">
                {modalProgram.highlights?.map((h, i) => (
                  <li key={i}>{h}</li>
                ))}
              </ul>
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* Contact Form Section */}
      <section id="contact-section" ref={contactRef} className="contact-section">
        <Form 
          onSubmit={handleContactFormSubmit}
          show={showContactForm}
          onClose={() => setShowContactForm(false)}
        />
      </section>

      {/* Scroll to Top Button */}
      <button 
        className={`scroll-to-top ${showScroll ? 'visible' : ''}`}
        onClick={scrollToTop}
      >
        ↑
      </button>

      <style>{`
        .image-wrapper:hover .image-overlay {
          opacity: 1;
        }
        .image-wrapper:hover img {
          transform: scale(1.05);
        }
        .category-nav .nav-link {
          color: #8d2020;
          font-weight: 600;
          margin: 0 5px;
          border-radius: 20px;
          border: 2px solid #8d2020;
        }
        .category-nav .nav-link.active {
          background: #8d2020;
          color: white;
        }
        .category-nav .nav-link:hover {
          background: #b71c1c;
          color: white;
        }
      `}</style>
    </div>
  );
};

export default Home;